import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CheckCircle2,
  Code2,
  ExternalLink,
  Eye,
  Layers,
  PauseCircle,
  PlayCircle,
  Rocket,
  ShieldCheck,
  Sparkles,
  Target,
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import SmartResponseSubNav from './SmartResponseSubNav';
import {
  buildSRPlanInMeta,
  fetchSRCompetitionsAndBrands,
  fetchSRMetaPlans,
  generateSRCampaignPlan,
  setSRPlanMetaStatus,
  SRCampaignPlanRecord,
  SRCompetition,
} from '../../services/smartResponseApi';

const REMA_ADSET_PREVIEWS = [
  {
    idx: 0,
    segment_key: 'young',
    label: 'Ad Set 1 (Meta 25–34): 18–34 år (Grøn)',
    ageButtonLabel: '18–34 år',
    adsetId: '120253959843670349',
    adId: '120254009057040349',
    creativeId: '1111854251459193',
    imageHash: '8f1915663986c35fd349efe7885c4f08',
    fbPreviewUrl: 'https://fb.me/1Wvyy834m90sljP',
    localImageUrl: '/smartresponse-assets/rema1000_age_25_34_1080x1080.png',
    dailyBudgetDkk: 90,
  },
  {
    idx: 1,
    segment_key: 'core',
    label: 'Ad Set 2 (Meta 35–49): 35–49 år (Grøn)',
    ageButtonLabel: '35–49 år',
    adsetId: '120253959844700349',
    adId: '120254009058150349',
    creativeId: '1093645883274036',
    imageHash: 'f69d3c1b21d3e2fa7221e8d639fd950d',
    fbPreviewUrl: 'https://fb.me/2c1ccdwo2jVtXUH',
    localImageUrl: '/smartresponse-assets/rema1000_age_35_49_1080x1080.png',
    dailyBudgetDkk: 135,
  },
  {
    idx: 2,
    segment_key: 'senior',
    label: 'Ad Set 3 (Meta 50–65): 50+ år (Grøn)',
    ageButtonLabel: '50+ år',
    adsetId: '120253959845320349',
    adId: '120254009059800349',
    creativeId: '1472211295109529',
    imageHash: 'bc2f9303555b22274f59fee9432718bf',
    fbPreviewUrl: 'https://fb.me/26VAhzKzEwEhiwr',
    localImageUrl: '/smartresponse-assets/rema1000_age_50_65_1080x1080.png',
    dailyBudgetDkk: 75,
  },
] as const;

export default function SmartResponseCampaignBuilder() {
  const { data } = useQuery({
    queryKey: ['sr-competitions-all'],
    queryFn: () => fetchSRCompetitionsAndBrands('all'),
  });

  const { data: plansData, refetch: refetchPlans } = useQuery({
    queryKey: ['sr-meta-plans'],
    queryFn: () => fetchSRMetaPlans('all'),
  });

  const competitions: SRCompetition[] = data?.competitions || [];
  const interestPacks = data?.interest_packs || [];
  const savedPlans: SRCampaignPlanRecord[] = plansData?.plans || data?.campaign_plans || [];

  const [selectedCompKey, setSelectedCompKey] = useState<string>(() => {
    try {
      const q = new URLSearchParams(window.location.search).get('comp_key');
      if (q) return q;
    } catch {
      // ignore
    }
    return 'no_onecall_rema1000_114927';
  });
  const [dailyBudgetDkk, setDailyBudgetDkk] = useState<number>(300);
  const [splitByAge, setSplitByAge] = useState<boolean>(true);
  const [splitByGender, setSplitByGender] = useState<boolean>(false);
  const [strictTargeting, setStrictTargeting] = useState<boolean>(true);
  const [ageMin, setAgeMin] = useState<number>(25);
  const [ageMax, setAgeMax] = useState<number>(65);
  const [genderFilter, setGenderFilter] = useState<'all' | 'women' | 'men'>('all');
  const [interestPack, setInterestPack] = useState<string>('broad_none');
  const [excludedZips, setExcludedZips] = useState<string>('');
  const [creativeTemplate, setCreativeTemplate] = useState<'age_buttons' | 'quiz_question' | 'gift_boxes'>('age_buttons');
  const [planResult, setPlanResult] = useState<any>(null);
  const [actionBanner, setActionBanner] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [inspectedPlan, setInspectedPlan] = useState<SRCampaignPlanRecord | null>(null);
  const [buildingId, setBuildingId] = useState<string | null>(null);

  // Interactive Ad Set & Step-by-Step Preview State
  const [previewAdsetIdx, setPreviewAdsetIdx] = useState<0 | 1 | 2>(1);
  const [previewStep, setPreviewStep] = useState<1 | 2 | 3>(1);
  const [selectedOperator, setSelectedOperator] = useState<string>('Telenor');
  const [selectedFirmPhone, setSelectedFirmPhone] = useState<string>('Nei, jeg betaler selv (Privat)');

  const selectedComp: SRCompetition | undefined =
    competitions.find((c) => c.comp_key === selectedCompKey) || competitions[0];

  useEffect(() => {
    if (!selectedComp) return;
    setAgeMin(selectedComp.target_age_min || 25);
    setAgeMax(selectedComp.target_age_max || 65);
    if (selectedComp.target_genders?.includes(2)) {
      setGenderFilter('women');
    } else if (selectedComp.target_genders?.includes(1)) {
      setGenderFilter('men');
    } else {
      setGenderFilter('all');
    }
    // Default to 100% Broad ('broad_none') so Meta uses Country + Age brackets + Exclusion pools only
    setInterestPack('broad_none');
  }, [selectedCompKey, competitions.length]);

  const handleBuildPreview = async (queueAction: boolean = false) => {
    if (!selectedComp) return;
    const genders = genderFilter === 'women' ? [2] : genderFilter === 'men' ? [1] : [];
    const res = await generateSRCampaignPlan({
      comp_key: selectedComp.comp_key,
      daily_budget_dkk: dailyBudgetDkk,
      split_by_age: splitByAge,
      split_by_gender: splitByGender && genderFilter === 'all',
      strict_targeting: strictTargeting,
      age_min: ageMin,
      age_max: ageMax,
      genders,
      interest_pack: interestPack,
      excluded_zips: excludedZips,
      creative_template: creativeTemplate,
      action: queueAction ? 'queue' : 'validate',
      dry_run: true,
    });
    setPlanResult(res.plan);
    if (queueAction) {
      setActionBanner({
        type: 'ok',
        text: `✓ Kampagneplan "${res.plan_record?.plan_key}" (${res.plan?.ad_sets?.length || 0} segmenterede Ad Sets) er valideret og lagt i Meta-køen!`,
      });
      refetchPlans();
    }
  };

  useEffect(() => {
    if (selectedComp) {
      handleBuildPreview(false);
    }
  }, [
    selectedCompKey,
    dailyBudgetDkk,
    splitByAge,
    splitByGender,
    strictTargeting,
    ageMin,
    ageMax,
    genderFilter,
    interestPack,
    excludedZips,
    creativeTemplate,
    competitions.length,
  ]);

  const handleExecuteBuild = async (planId: string, mode: 'dry_run' | 'live') => {
    setBuildingId(`${planId}_${mode}`);
    setActionBanner(null);
    try {
      const res: any = await buildSRPlanInMeta(planId, mode);
      if (res.success === false) {
        setActionBanner({
          type: 'err',
          text: `Fejl ved oprettelse i Meta (${mode.toUpperCase()}): ${res.error}`,
        });
      } else {
        setActionBanner({
          type: 'ok',
          text:
            mode === 'live'
              ? `✓ Kampagne "${planId}" er oprettet LIVE i PAUSED på act_995004339585761 og Page 1420182721178198!`
              : `✓ Dry-Run Build gennemført for "${planId}" (${(res.calls || []).length} Graph API v25.0 kald genereret).`,
        });
      }
      if (res.plan_record) {
        setInspectedPlan(res.plan_record);
      }
      await refetchPlans();
    } finally {
      setBuildingId(null);
    }
  };

  const handleStatusChange = async (planId: string, action: 'activate' | 'pause') => {
    setActionBanner(null);
    const res: any = await setSRPlanMetaStatus(planId, action);
    setActionBanner({
      type: 'ok',
      text: `✓ Kampagnestatus for "${planId}" ændret til ${res.meta_status || action.toUpperCase()}.`,
    });
    await refetchPlans();
  };

  const isOneCall = ['114927', '114889', '114874'].includes(selectedComp?.sr_campaign_id || '');
  const isRema1000 = selectedComp?.comp_key === 'no_onecall_rema1000_114927';
  const activeAdsetPreview = REMA_ADSET_PREVIEWS[previewAdsetIdx];
  const buttonLabels =
    creativeTemplate === 'quiz_question' && selectedComp?.qualifying_questions?.[0]?.options?.length
      ? selectedComp.qualifying_questions[0].options.slice(0, 3)
      : creativeTemplate === 'gift_boxes'
      ? ['Gaveæske #1', 'Gaveæske #2', 'Gaveæske #3']
      : ['18–34 år', '35–49 år', '50+ år'];

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto">
        <SmartResponseSubNav />

        {/* Header */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6 shadow-sm flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider rounded bg-[#0F3F34] text-white">
                Meta Graph API v25.0 Builder (Som SimplyLoan — med Fuld Segmentering)
              </span>
              <span className="text-xs text-emerald-700 font-semibold">
                ● Ad Account: act_995004339585761 • Page: 1420182721178198 • special_ad_categories: []
              </span>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mt-1">
              AI Kampagnekø, Alders-/Køns-Segmentering, Instant Form & Kreativ-Motor
            </h2>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <a
              href="https://premieportal.com/meta-preview"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#1877F2] hover:bg-[#166FE5] text-white text-xs font-bold shadow-sm"
            >
              Åbn Fuld Step-by-Step Simulator <ExternalLink size={14} />
            </a>
            <button
              onClick={() => handleBuildPreview(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#F07824] hover:opacity-95 text-white text-xs font-bold shadow-sm"
            >
              <Sparkles size={15} />
              Validér & Læg i Meta-Kø (PAUSED)
            </button>
          </div>
        </div>

        {actionBanner && (
          <div
            className={`mb-6 p-4 rounded-xl border text-xs font-semibold flex items-center justify-between ${
              actionBanner.type === 'ok'
                ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
                : 'bg-red-50 border-red-300 text-red-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <CheckCircle2 size={16} />
              <span>{actionBanner.text}</span>
            </div>
            <button onClick={() => setActionBanner(null)} className="underline ml-3">
              Luk
            </button>
          </div>
        )}

        {/* SECTION 1: Live Meta Campaign Queue (Like SimplyLoan) */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2">
              <Rocket size={18} className="text-[#0F3F34]" />
              <h3 className="text-base font-bold text-gray-900">
                Kampagneplaner & Meta Graph API Eksekveringskø (act_995004339585761)
              </h3>
            </div>
            <span className="text-xs text-gray-500">
              Alle kampagner oprettes i <code>PAUSED</code> (intet spend før du klikker Aktivér)
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-200 text-[11px] font-bold uppercase text-gray-500">
                  <th className="py-2.5 pr-3">Plan Key & Kampagnenavn</th>
                  <th className="py-2.5 px-2">Konkurrence</th>
                  <th className="py-2.5 px-2">Ad Sets & Live Previews</th>
                  <th className="py-2.5 px-2 text-right">Dagsbudget</th>
                  <th className="py-2.5 px-2">Status</th>
                  <th className="py-2.5 pl-3 text-right">Meta Graph API Handlinger</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-xs">
                {savedPlans.map((p) => {
                  const adsetsCount = p.plan?.ad_sets?.length || 0;
                  const isBuilt = ['dry_run_built', 'built_paused', 'active', 'paused'].includes(p.status);
                  const isRemaPlan = p.comp_key === 'no_onecall_rema1000_114927';
                  return (
                    <tr key={p.plan_id} className="hover:bg-gray-50">
                      <td className="py-3 pr-3">
                        <p className="font-bold text-gray-900">{p.name}</p>
                        <p className="text-[11px] text-gray-400 font-mono mt-0.5">
                          {p.plan_key} • {p.ad_account_id} • Page {p.page_id}
                        </p>
                        {p.meta_objects?.campaign_id && (
                          <p className="text-[11px] text-emerald-700 font-mono mt-0.5">
                            Campaign ID: {p.meta_objects.campaign_id} • Form ID: {p.meta_objects.lead_form_id}
                          </p>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        <span className="font-semibold text-gray-800">{p.comp_key}</span>
                        <span className="block text-[11px] text-gray-400">productId={p.sr_campaign_id} • REMA 1000 5.000 kr.</span>
                      </td>
                      <td className="py-3 px-2">
                        <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-800 font-bold">
                          {adsetsCount} segmenterede Ad Sets
                        </span>
                        {isRemaPlan && (
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {REMA_ADSET_PREVIEWS.map((ap) => (
                              <a
                                key={ap.adId}
                                href={ap.fbPreviewUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 font-bold text-[10px] hover:bg-emerald-100"
                              >
                                {ap.ageButtonLabel} (fb.me) <ExternalLink size={10} />
                              </a>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-2 text-right font-bold text-gray-900">
                        {p.daily_budget_dkk} kr./dag
                      </td>
                      <td className="py-3 px-2">
                        <span
                          className={`px-2 py-0.5 rounded font-bold uppercase text-[10px] ${
                            p.status === 'active'
                              ? 'bg-emerald-100 text-emerald-800'
                              : p.status === 'built_paused' || p.status === 'dry_run_built'
                              ? 'bg-blue-100 text-blue-800'
                              : p.status === 'failed'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {p.status}
                        </span>
                      </td>
                      <td className="py-3 pl-3 text-right">
                        <div className="inline-flex flex-wrap items-center justify-end gap-1.5">
                          <a
                            href="https://premieportal.com/meta-preview"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#1877F2] hover:bg-[#166FE5] text-white font-bold"
                          >
                            Step-by-Step Preview <ExternalLink size={12} />
                          </a>
                          <button
                            onClick={() => handleExecuteBuild(p.plan_id, 'dry_run')}
                            disabled={buildingId === `${p.plan_id}_dry_run`}
                            className="px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 font-bold text-gray-700"
                          >
                            {buildingId === `${p.plan_id}_dry_run` ? 'Bygger...' : 'Byg (Dry-Run)'}
                          </button>
                          <button
                            onClick={() => handleExecuteBuild(p.plan_id, 'live')}
                            disabled={buildingId === `${p.plan_id}_live`}
                            className="px-2.5 py-1.5 rounded-lg bg-[#0F3F34] hover:opacity-95 text-white font-bold"
                          >
                            {buildingId === `${p.plan_id}_live` ? 'Opretter...' : 'Byg LIVE i Meta (PAUSED)'}
                          </button>
                          {isBuilt && (
                            <>
                              <button
                                onClick={() => handleStatusChange(p.plan_id, 'activate')}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                              >
                                <PlayCircle size={13} /> Aktivér
                              </button>
                              <button
                                onClick={() => handleStatusChange(p.plan_id, 'pause')}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-bold"
                              >
                                <PauseCircle size={13} /> Pause
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => setInspectedPlan(inspectedPlan?.plan_id === p.plan_id ? null : p)}
                            className="px-2 py-1.5 rounded-lg text-gray-500 hover:text-gray-900 underline"
                          >
                            Inspicér API
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Graph API Call Inspector Drawer */}
          {inspectedPlan && (
            <div className="mt-4 p-4 rounded-xl bg-gray-900 text-gray-100 text-xs">
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-emerald-400">
                  Meta Graph API v25.0 Build-Log for {inspectedPlan.plan_key} (Konto: {inspectedPlan.ad_account_id}, Page: {inspectedPlan.page_id})
                </span>
                <button
                  onClick={() => setInspectedPlan(null)}
                  className="text-gray-400 hover:text-white underline"
                >
                  Luk Inspector
                </button>
              </div>
              {inspectedPlan.last_error && (
                <p className="p-2.5 mb-2 rounded bg-red-900/50 border border-red-500 text-red-200">
                  {inspectedPlan.last_error}
                </p>
              )}
              {inspectedPlan.meta_objects?.campaign_id && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 p-2.5 rounded bg-gray-800 border border-gray-700 font-mono text-[11px]">
                  <div>
                    <span className="text-gray-400 block">Campaign ID:</span>
                    <span className="text-emerald-300">{inspectedPlan.meta_objects.campaign_id}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 block">Instant Form ID:</span>
                    <span className="text-emerald-300">{inspectedPlan.meta_objects.lead_form_id}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 block">Image Hash:</span>
                    <span className="text-emerald-300">{inspectedPlan.meta_objects.image_hash}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 block">Ad Sets / Ads:</span>
                    <span className="text-emerald-300">
                      {(inspectedPlan.meta_objects.adsets || []).length} Ad Sets / {(inspectedPlan.meta_objects.ads || []).length} Ads
                    </span>
                  </div>
                </div>
              )}
              {inspectedPlan.meta_objects?.ads && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                  {inspectedPlan.meta_objects.ads.map((adItem) => (
                    <div key={adItem.id} className="p-2.5 rounded bg-gray-800 border border-gray-700 font-mono text-[11px]">
                      <p className="text-white font-bold">{adItem.name}</p>
                      <p className="text-gray-400 mt-0.5">Ad ID: {adItem.id}</p>
                      <p className="text-gray-400">Creative ID: {adItem.creative_id}</p>
                      {adItem.preview_url && (
                        <a
                          href={adItem.preview_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-emerald-400 underline mt-1"
                        >
                          Åbn Meta Preview ({adItem.preview_url}) <ExternalLink size={11} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="space-y-1.5 max-h-60 overflow-y-auto font-mono text-[11px]">
                {(inspectedPlan.build_calls || []).map((c) => (
                  <div key={c.step} className="p-2 rounded bg-gray-800/80 border border-gray-700">
                    <span className="text-amber-400 font-bold">
                      #{c.step} {c.method} /v25.0/{c.path}
                    </span>{' '}
                    — <span className="text-gray-200">{c.summary}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* SECTION 2: Interactive Campaign & Segmentation Configurator */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column: Controls */}
          <div className="lg:col-span-5 space-y-5">
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Target size={17} className="text-[#0F3F34]" />
                <h3 className="text-sm font-bold text-gray-900">
                  1. Vælg Konkurrence & Målgruppe-Segmentering
                </h3>
              </div>

              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Aktiv SmartResponse Konkurrence
              </label>
              <select
                value={selectedCompKey}
                onChange={(e) => setSelectedCompKey(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-900 mb-3"
              >
                {competitions.map((c) => (
                  <option key={c.comp_key} value={c.comp_key}>
                    [{c.geo.toUpperCase()}] {c.prize_name} (productId={c.sr_campaign_id} • {c.payout_dkk} DKK)
                  </option>
                ))}
              </select>

              {selectedComp && (
                <div className="p-3 rounded-lg bg-[#F4F8F6] border border-emerald-200 text-xs space-y-1 mb-4">
                  <p className="font-bold text-[#0F3F34]">{selectedComp.title}</p>
                  <p className="text-gray-600">
                    Tilknyttet eksklusionspulje:{' '}
                    {selectedComp.exclusion_pools.map((p) => (
                      <code key={p} className="px-1.5 py-0.5 rounded bg-white border border-emerald-200 mr-1">
                        {p}
                      </code>
                    ))}
                  </p>
                  {isOneCall && (
                    <p className="text-emerald-800 font-semibold pt-1">
                      ✓ Lars' OneCall-regel aktiv: Postnr., adresse og e-mail spørges IKKE i formularen (vi sender{' '}
                      <code>{'{tlf}@gmail.com'}</code> automatisk i felt 148).
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Dagsbudget (DKK)
                  </label>
                  <input
                    type="number"
                    value={dailyBudgetDkk}
                    onChange={(e) => setDailyBudgetDkk(Number(e.target.value))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Min. Alder</label>
                  <input
                    type="number"
                    min={18}
                    max={65}
                    value={ageMin}
                    onChange={(e) => setAgeMin(Number(e.target.value))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Max. Alder</label>
                  <input
                    type="number"
                    min={25}
                    max={65}
                    value={ageMax}
                    onChange={(e) => setAgeMax(Number(e.target.value))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold"
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Køns-segmentering (Modsat SimplyLoan har vi fri køns-kontrol her)
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(
                    [
                      { id: 'all', label: 'Alle køn' },
                      { id: 'women', label: 'Kun Kvinder (2)' },
                      { id: 'men', label: 'Kun Mænd (1)' },
                    ] as const
                  ).map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => setGenderFilter(g.id)}
                      className={`py-1.5 rounded-lg text-xs font-semibold border ${
                        genderFilter === g.id
                          ? 'bg-[#0F3F34] text-white border-[#0F3F34]'
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-3">
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Interesse-Segment (`flexible_spec`)
                </label>
                <select
                  value={interestPack}
                  onChange={(e) => setInterestPack(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-900"
                >
                  {interestPacks.map((ip: any) => (
                    <option key={ip.key} value={ip.key}>
                      {ip.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Ekskludér Postnumre (valgfrit, kommasepareret)
                </label>
                <input
                  type="text"
                  placeholder="Fx 0150, 0151"
                  value={excludedZips}
                  onChange={(e) => setExcludedZips(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-mono"
                />
              </div>

              <div className="space-y-2 pt-2 border-t border-gray-100">
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={splitByAge}
                    onChange={(e) => setSplitByAge(e.target.checked)}
                    className="rounded text-[#0F3F34]"
                  />
                  Split automatisk i 3 Alders-Ad Sets (25–34, 35–49, 50–65 år) med matchende grøn knap
                </label>

                {genderFilter === 'all' && (
                  <label className="flex items-center gap-2 text-xs font-semibold text-gray-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={splitByGender}
                      onChange={(e) => setSplitByGender(e.target.checked)}
                      className="rounded text-[#0F3F34]"
                    />
                    Split hvert alders-adset i Kvinder vs. Mænd (2× Ad Sets for ren CPL-test)
                  </label>
                )}

                <label className="flex items-center gap-2 text-xs font-semibold text-gray-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={strictTargeting}
                    onChange={(e) => setStrictTargeting(e.target.checked)}
                    className="rounded text-[#0F3F34]"
                  />
                  Deaktivér Advantage+ Audience (`advantage_audience: 0`) for skarp aldersgrænse
                </label>
              </div>
            </div>

            {/* Creative Template Selector */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Eye size={17} className="text-[#F07824]" />
                <h3 className="text-sm font-bold text-gray-900">
                  2. Vælg Interaktiv Kreativ-Skabelon (1080×1080)
                </h3>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {(
                  [
                    { id: 'age_buttons', label: 'Alders-Knapper (Grøn mål-knap)' },
                    { id: 'quiz_question', label: 'Operatør-Quiz' },
                    { id: 'gift_boxes', label: '3 Gaveæsker' },
                  ] as const
                ).map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setCreativeTemplate(t.id)}
                    className={`py-2 px-2 rounded-lg text-xs font-bold border ${
                      creativeTemplate === t.id
                        ? 'bg-[#0F3F34] text-white border-[#0F3F34]'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column: Live Creative Preview + Step-by-Step Simulator + Ad Sets + Compliance */}
          <div className="lg:col-span-7 space-y-5">
            {selectedComp && (
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div>
                    <span className="text-xs font-bold uppercase text-gray-500 block">
                      Live Facebook Feed & Instant Form Preview ({selectedComp.geo.toUpperCase()} — Page 1420182721178198)
                    </span>
                    <span className="text-[11px] text-gray-500">
                      Vælg Ad Set herunder for at se den matchende grønne aldersknap og hele brugerrejsen trin for trin
                    </span>
                  </div>
                  <a
                    href="https://premieportal.com/meta-preview"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#1877F2] hover:bg-[#166FE5] text-white text-[11px] font-bold"
                  >
                    Åbn Fuld Simulator <ExternalLink size={12} />
                  </a>
                </div>

                {/* Ad Set Target Age Switcher (Green Button Selector) */}
                <div className="mb-4 p-2.5 rounded-xl bg-slate-900 text-white">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-400">
                      Vælg Ad Set (Skifter Grøn Målgruppe-Knap på 1080×1080 Billedet):
                    </span>
                    {isRema1000 && (
                      <a
                        href={activeAdsetPreview.fbPreviewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-300 hover:underline"
                      >
                        Åbn dette Ad Set i Meta ({activeAdsetPreview.fbPreviewUrl.replace('https://', '')}) <ExternalLink size={11} />
                      </a>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {REMA_ADSET_PREVIEWS.map((ap) => (
                      <button
                        key={ap.idx}
                        type="button"
                        onClick={() => {
                          setPreviewAdsetIdx(ap.idx as 0 | 1 | 2);
                        }}
                        className={`py-2 px-2.5 rounded-lg text-xs font-extrabold transition-all flex items-center justify-between ${
                          previewAdsetIdx === ap.idx
                            ? 'bg-[#22C55E] text-white shadow-sm'
                            : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        <span>{ap.label}</span>
                        <span className="text-[10px] opacity-90">{ap.dailyBudgetDkk} kr/d</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Step Switcher */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {[
                    { step: 1 as const, label: 'Trin 1: Facebook Annonce' },
                    { step: 2 as const, label: 'Trin 2: Meta Instant Form' },
                    { step: 3 as const, label: 'Trin 3: Tak & Offerwall' },
                  ].map((s) => (
                    <button
                      key={s.step}
                      type="button"
                      onClick={() => setPreviewStep(s.step)}
                      className={`py-2 px-3 rounded-lg text-xs font-bold border transition-colors ${
                        previewStep === s.step
                          ? 'bg-[#1877F2] text-white border-[#1877F2]'
                          : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                  {/* Left Sub-Card: Authentic Facebook Mobile Feed Ad */}
                  <div className="rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm">
                    {/* FB Post Header */}
                    <div className="p-3 flex items-center gap-2.5 border-b border-gray-100">
                      <img
                        src="/smartresponse-assets/no_facebook_profile_800x800.png"
                        alt="PremiePortalen"
                        className="w-9 h-9 rounded-full object-cover border border-gray-200"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-gray-900 leading-tight">PremiePortalen</p>
                        <p className="text-[11px] text-gray-500">Sponsoreret · 🌐</p>
                      </div>
                      <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 font-mono text-[10px] font-bold">
                        {isRema1000 ? `Ad ${activeAdsetPreview.adId.slice(-6)}` : selectedComp.geo.toUpperCase()}
                      </span>
                    </div>

                    {/* FB Primary Text */}
                    <div className="px-3.5 py-2.5 text-xs text-gray-800 space-y-1 bg-white">
                      <p className="font-bold">
                        🎁 {isRema1000 ? 'Vinn et gavekort på 5.000 kr. til REMA 1000!' : selectedComp.title}
                      </p>
                      <p className="text-gray-600 text-[11px] leading-relaxed">
                        Klikk på din aldersgruppe på bildet under og svar på 2 raske spørsmål – det tar kun 30 sekunder å delta.
                      </p>
                      <p className="text-[11px] text-gray-700">
                        ✅ Gratis deltakelse • ✅ Vinneren trekkes {selectedComp.terms_draw_date}
                      </p>
                      {isRema1000 && (
                        <p className="text-[11px] text-gray-500 font-medium">
                          * Konkurransen er ikke sponset av Rema1000
                        </p>
                      )}
                    </div>

                    {/* 1:1 1080x1080 Ad Creative */}
                    {isRema1000 && creativeTemplate === 'age_buttons' ? (
                      <div
                        onClick={() => setPreviewStep(2)}
                        className="relative cursor-pointer group border-y border-gray-100"
                        title="Klik for at åbne Trin 2 (Meta Instant Form)"
                      >
                        <img
                          src={activeAdsetPreview.localImageUrl}
                          alt={`REMA 1000 5.000 kr. — ${activeAdsetPreview.label}`}
                          className="w-full h-auto block"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-end justify-center pb-3">
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity px-3 py-1 rounded-full bg-slate-900/90 text-white text-[11px] font-bold shadow">
                            Klik for at åbne Instant Form (Trin 2) →
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="p-5 bg-slate-50 border-y border-gray-200 flex flex-col justify-between min-h-[290px]">
                        <div className="text-center">
                          <span className="inline-block px-3 py-0.5 rounded-full bg-red-600 text-white text-[11px] font-extrabold mb-2">
                            VÆRDI {selectedComp.prize_value_dkk.toLocaleString('da-DK')} KR.
                          </span>
                          <h4 className="text-sm font-extrabold text-slate-900 leading-snug">
                            {selectedComp.title}
                          </h4>
                          <p className="text-[11px] text-slate-600 mt-1">{selectedComp.subtitle}</p>
                        </div>

                        <div className="space-y-2 my-4">
                          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 text-center">
                            Klik på din aldersgruppe for at deltage:
                          </p>
                          {buttonLabels.map((lbl, idx) => {
                            const isTargetGreen = idx === previewAdsetIdx;
                            return (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => setPreviewStep(2)}
                                className={`w-full py-2.5 px-3.5 rounded-xl text-white font-extrabold text-xs flex items-center justify-between shadow-sm transition-transform hover:scale-[1.01] ${
                                  isTargetGreen ? 'bg-[#22C55E]' : 'bg-[#0F172A]'
                                }`}
                              >
                                <span>{lbl}</span>
                                <span>→</span>
                              </button>
                            );
                          })}
                        </div>

                        <p className="text-[10px] text-slate-500 text-center">
                          Gratis deltakelse • Trekning {selectedComp.terms_draw_date}
                        </p>
                      </div>
                    )}

                    {/* FB Link Preview Bar */}
                    <div className="p-3 bg-gray-50 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                          PREMIEPORTAL.COM
                        </p>
                        <p className="text-xs font-extrabold text-gray-900 truncate">
                          {isRema1000 ? 'Vinn REMA 1000 gavekort på 5.000 kr.' : selectedComp.prize_name}
                        </p>
                        <p className="text-[11px] text-gray-500 truncate">Gratis deltakelse (18+ år)</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPreviewStep(2)}
                        className="shrink-0 px-3.5 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-900 font-bold text-xs"
                      >
                        Deltak nå
                      </button>
                    </div>
                  </div>

                  {/* Right Sub-Card: Interactive Step 2 (Instant Form) & Step 3 (Thank You Offerwall) */}
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 flex flex-col justify-between text-xs min-h-[420px]">
                    {previewStep !== 3 ? (
                      <div>
                        <div className="flex items-center justify-between mb-2.5 pb-2 border-b border-gray-200">
                          <div>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-[#1877F2] block">
                              Trin 2 • Meta Instant Form (ID: 1548544003699895)
                            </span>
                            <h4 className="font-extrabold text-gray-900 text-sm">
                              {isRema1000 ? 'Vinn et gavekort til REMA 1000 på 5.000 kr.' : selectedComp.prize_name}
                            </h4>
                          </div>
                          <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold text-[10px]">
                            Valgt: {activeAdsetPreview.ageButtonLabel}
                          </span>
                        </div>

                        <div className="space-y-3">
                          {/* Question 1: Individual Operators */}
                          <div className="p-3 rounded-lg bg-white border border-gray-200">
                            <label className="block font-bold text-gray-900 mb-1">
                              1. {selectedComp.qualifying_questions?.[0]?.label || 'Hvilken mobiloperatør har du i dag?'}
                            </label>
                            <select
                              value={selectedOperator}
                              onChange={(e) => setSelectedOperator(e.target.value)}
                              className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-900"
                            >
                              {(
                                selectedComp.qualifying_questions?.[0]?.options || [
                                  'Telenor',
                                  'Talkmore',
                                  'Ice',
                                  'Chili Mobil',
                                  'Happybytes',
                                  'PlussMobil',
                                  'Fjordkraft',
                                  'Telia',
                                  'OneCall',
                                  'MyCall',
                                  'Annet',
                                ]
                              ).map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                            {['OneCall', 'Telia', 'MyCall'].includes(selectedOperator) ? (
                              <p className="text-[11px] text-red-600 font-semibold mt-1">
                                ⚠ Filtreres fra ved webhook (eksisterende OneCall/Telia/MyCall kunde sendes ikke til FeedOrder 114927).
                              </p>
                            ) : (
                              <p className="text-[11px] text-emerald-700 font-semibold mt-1">
                                ✓ Kvalificeret operatør til OneCall (felt 7753).
                              </p>
                            )}
                          </div>

                          {/* Question 2: Private vs Company Phone */}
                          <div className="p-3 rounded-lg bg-white border border-gray-200">
                            <label className="block font-bold text-gray-900 mb-1.5">
                              2. Har du firmabetalt mobilabonnement?
                            </label>
                            <div className="grid grid-cols-1 gap-1.5">
                              {['Nei, jeg betaler selv (Privat)', 'Ja, arbeidsgiver betaler (Bedrift)'].map((opt) => (
                                <button
                                  key={opt}
                                  type="button"
                                  onClick={() => setSelectedFirmPhone(opt)}
                                  className={`text-left px-2.5 py-1.5 rounded-lg border font-semibold text-xs ${
                                    selectedFirmPhone === opt
                                      ? 'bg-emerald-50 border-emerald-500 text-emerald-900'
                                      : 'bg-gray-50 border-gray-200 text-gray-700'
                                  }`}
                                >
                                  {opt}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Contact Fields */}
                          <div className="p-3 rounded-lg bg-white border border-gray-200 space-y-1.5">
                            <p className="font-bold text-gray-900">
                              3. Kontaktinfo (Auto-udfyldt af Meta + Fødselsdato):
                            </p>
                            <div className="grid grid-cols-2 gap-1.5">
                              <div className="px-2.5 py-1.5 rounded bg-gray-50 border border-gray-200 text-gray-700">
                                <span className="text-[10px] text-gray-400 block">Fornavn (522)</span>
                                <span className="font-semibold">Kari</span>
                              </div>
                              <div className="px-2.5 py-1.5 rounded bg-gray-50 border border-gray-200 text-gray-700">
                                <span className="text-[10px] text-gray-400 block">Etternavn (523)</span>
                                <span className="font-semibold">Nordmann</span>
                              </div>
                              <div className="px-2.5 py-1.5 rounded bg-gray-50 border border-gray-200 text-gray-700">
                                <span className="text-[10px] text-gray-400 block">Mobilnr. 8 siffer (117)</span>
                                <span className="font-semibold">98765432</span>
                              </div>
                              <div className="px-2.5 py-1.5 rounded bg-gray-50 border border-gray-200 text-gray-700">
                                <span className="text-[10px] text-gray-400 block">Fødselsdato (119)</span>
                                <span className="font-semibold">15.05.1985</span>
                              </div>
                            </div>
                            {isOneCall && (
                              <div className="mt-1.5 p-2 rounded-lg bg-emerald-50 border border-dashed border-emerald-400 text-[11px] text-emerald-900">
                                <span className="font-extrabold block text-emerald-800">
                                  Skjult E-postfelt (Felt 148 — udfyldes IKKE af brugeren):
                                </span>
                                <code className="font-mono font-bold text-emerald-950">98765432@gmail.com</code> (unik pr. telefonnummer)
                              </div>
                            )}
                          </div>

                          {/* Consent Checkbox */}
                          <div className="p-2.5 rounded-lg bg-white border border-gray-200 text-[11px] text-gray-600">
                            <label className="flex items-start gap-2">
                              <input type="checkbox" checked readOnly className="mt-0.5 rounded text-[#1877F2]" />
                              <span>
                                <strong>Samtykke til OneCall:</strong> Ja, jeg er over 18 år og samtykker til at{' '}
                                <strong>OneCall (Telia Norge AS)</strong> kan kontakte meg på telefon, SMS og e-post med gode tilbud på mobilabonnement.
                              </span>
                            </label>
                          </div>

                          <button
                            type="button"
                            onClick={() => setPreviewStep(3)}
                            className="w-full py-2.5 px-4 rounded-xl bg-[#1877F2] hover:bg-[#166FE5] text-white font-extrabold text-xs shadow-sm"
                          >
                            Send inn og se Trin 3 (Tak-skærm & Offerwall) →
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                            Trin 3 • Meta Tak-Skærm → Auto-Krydssalg på PremiePortalen
                          </span>
                          <button
                            type="button"
                            onClick={() => setPreviewStep(2)}
                            className="text-[11px] text-[#1877F2] font-bold underline"
                          >
                            ← Tilbage til Trin 2
                          </button>
                        </div>

                        <div className="p-4 rounded-xl bg-white border border-emerald-200 text-center space-y-2">
                          <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto font-extrabold text-lg">
                            ✓
                          </div>
                          <h4 className="text-sm font-extrabold text-gray-900">
                            Tusen takk for din deltakelse, Kari! 🎁
                          </h4>
                          <p className="text-xs text-gray-600">
                            Din deltakelse i trekningen om <strong>REMA 1000 gavekort på 5.000 kr.</strong> er registrert. Vinneren trekkes 22. desember 2026.
                          </p>
                          <p className="text-xs font-semibold text-emerald-800 bg-emerald-50 p-2.5 rounded-lg">
                            Vil du øke vinnersjansene dine? Vi har samlet flere gratis ekstrakonkurranser til deg på PremiePortalen (OneCall er automatisk skjult så vi undgår dubletter):
                          </p>
                          <a
                            href={`https://premieportal.com/konkurrencer/?done=${selectedComp.comp_key}&geo=${selectedComp.geo}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 px-4 rounded-xl bg-[#22C55E] hover:bg-emerald-600 text-white font-extrabold text-xs shadow-sm"
                          >
                            Se flere gratis konkurranser → <ExternalLink size={13} />
                          </a>
                        </div>

                        <div className="p-3 rounded-lg bg-slate-900 text-slate-200 font-mono text-[11px] space-y-1">
                          <p className="text-emerald-400 font-bold">Real-Time Webhook → FeedOrder.aspx:</p>
                          <p>POST https://webservice.smartresponse-media.com/FeedOrder.aspx</p>
                          <p>sourceId=100168 &amp; partnerId=1336 &amp; productId=114927</p>
                          <p>522=Kari &amp; 523=Nordmann &amp; 117=91234567 &amp; 148=91234567@gmail.com</p>
                        </div>
                      </div>
                    )}

                    <div className="mt-3 pt-2 border-t border-gray-200 text-[11px] text-gray-500 flex flex-wrap items-center justify-between gap-2">
                      <span>
                        Privatlivs- &amp; vilkårslink:{' '}
                        <a
                          href={planResult?.instant_form?.privacy_policy?.url || `https://premieportal.com/partnere/${selectedComp.comp_key}/`}
                          target="_blank"
                          rel="noreferrer"
                          className="font-mono text-[#0F3F34] underline"
                        >
                          /partnere/{selectedComp.comp_key}/
                        </a>
                      </span>
                      <a
                        href="https://premieportal.com/meta-preview"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-bold text-[#1877F2] hover:underline inline-flex items-center gap-1"
                      >
                        Fuldskærms-preview <ExternalLink size={11} />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Generated Ad Sets Matrix */}
            {planResult && (
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Layers size={17} className="text-[#0F3F34]" />
                  <h3 className="text-sm font-bold text-gray-900">
                    Genererede Ad Sets ({planResult.ad_sets?.length || 0} stk.) med Eksklusionsbinding
                  </h3>
                </div>

                <div className="space-y-2.5">
                  {(planResult.ad_sets || []).map((adset: any, idx: number) => {
                    const matchedRema = isRema1000 ? REMA_ADSET_PREVIEWS[idx] : undefined;
                    return (
                      <div
                        key={adset.segment_key}
                        className="p-3 rounded-lg border border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-2 text-xs"
                      >
                        <div>
                          <p className="font-bold text-gray-900">{adset.name}</p>
                          <p className="text-gray-500 mt-0.5">
                            Alder: <strong>{adset.targeting?.age_min}–{adset.targeting?.age_max} år</strong> •{' '}
                            Grøn knap: <strong className="text-emerald-700">{matchedRema?.ageButtonLabel || `${adset.targeting?.age_min}–${adset.targeting?.age_max} år`}</strong> •{' '}
                            Ekskluderer:{' '}
                            {(adset.targeting?.excluded_custom_audiences || [])
                              .map((ca: any) => ca.id)
                              .join(', ')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {matchedRema && (
                            <a
                              href={matchedRema.fbPreviewUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white border border-gray-300 text-gray-800 font-bold hover:bg-gray-100"
                            >
                              Meta Preview <ExternalLink size={11} />
                            </a>
                          )}
                          <span className="px-2.5 py-1 rounded-md bg-[#0F3F34] text-white font-bold">
                            {adset.daily_budget_dkk} kr./dag
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Compliance Gate */}
            {planResult?.compliance && (
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={18} className="text-emerald-600" />
                    <h3 className="text-sm font-bold text-gray-900">
                      Pre-Flight Validering ({planResult.compliance.blocker_count} blokere)
                    </h3>
                  </div>
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">
                    KLAR TIL META BUILD
                  </span>
                </div>

                <div className="space-y-2">
                  {(planResult.compliance.checks || []).map((chk: any) => (
                    <div
                      key={chk.code}
                      className="flex items-start gap-2 text-xs p-2.5 rounded-lg bg-gray-50 border border-gray-200"
                    >
                      <CheckCircle2 size={15} className="text-emerald-600 shrink-0 mt-0.5" />
                      <div>
                        <span className="font-bold text-gray-900">{chk.code}: </span>
                        <span className="text-gray-600">{chk.message}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <details className="mt-4">
                  <summary className="cursor-pointer text-xs font-bold text-[#0F3F34] inline-flex items-center gap-1">
                    <Code2 size={14} /> Vis komplet Meta Marketing API v25.0 JSON Spec
                  </summary>
                  <pre className="mt-2 p-3 rounded-lg bg-gray-900 text-emerald-300 text-[11px] overflow-x-auto max-h-72">
                    {JSON.stringify(planResult, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
